import { CommonModule } from '@angular/common';
import { Component, Inject, inject, Injector } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContactInfoCardComponent } from '@vendasta/galaxy/contact-info-card';
import { Row } from '@vendasta/galaxy/table';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  Association,
  AssociationCardComponent,
  AssociationsDialogData,
  BrandingService,
  CardData,
  combineAssociationsWithChanges,
  CommonAssociationCardContentComponent,
  CompanyTableCustomizationService,
  CONTACT_PROFILE_CARD_DATA_TOKEN,
  ContactTableCustomizationService,
  CrmAssociationService,
  CrmDependencies,
  CrmFieldService,
  CrmInjectionToken,
  CrmObjectService,
  CrmPlatformApiService,
  DefaultAssociationActionsService,
  ListObjectTypeDialogData,
  PAGE_ROUTES,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
  RemoveAssociationData,
  RemoveAssociationDialogComponent,
  SelectAssociationsModalComponent,
  SelectObjectTypeModalComponent,
  StandardIds,
  SystemFieldIds,
  tableFiltersInjectionTokenGenerator,
  TagsComponent,
} from '@galaxy/crm/static';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  AccessBusinessAppComponent,
  AccessBusinessAppService,
} from '../association-cards/access-business-app/access-business-app.component';
import { AppAccess } from '../association-cards/access-business-app/access-business-app.service';

interface ContactToCompanyAssociationsCardData extends CardData {
  associationId: string;
  contactName: string;
  businessAppAccess: AppAccess;
}

@Component({
  selector: 'app-pcc-crm-contact-to-company-associations-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatExpansionModule,
    AssociationCardComponent,
    ContactInfoCardComponent,
    CommonAssociationCardContentComponent,
    AccessBusinessAppComponent,
    TagsComponent,
    MatButtonModule,
    MatIconModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatMenuModule,
  ],
  providers: [
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
    },
    AccessBusinessAppService,
    BrandingService,
    CrmPlatformApiService,
  ],
  templateUrl: './pcc-contact-to-company-associations-card.component.html',
  styleUrls: ['./pcc-contact-to-company-associations-card.component.scss'],
})
export class PccContactToCompanyAssociationsCardComponent {
  data = inject(CONTACT_PROFILE_CARD_DATA_TOKEN);
  getAssociations$: Observable<Association[]>;
  loadAssociations$$ = new BehaviorSubject<Association[]>([]);
  preSelectedRows$$ = new BehaviorSubject<Association[]>([]);
  fullName$: Observable<string>;
  modalData$: Observable<AssociationsDialogData>;
  canOpenModal = false;
  companies$: Observable<ContactToCompanyAssociationsCardData[]>;
  removedAssociationIds$$ = new BehaviorSubject<string[]>([]);
  hasAssociation = false;
  existingAssociations$: Observable<Association[]>;
  changedAssociations$$ = new BehaviorSubject<Association[]>([]);

  private readonly injector = inject(Injector);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectService: CrmObjectService,
    private readonly fieldService: CrmFieldService,
    private readonly associationService: CrmAssociationService,
    private readonly dialog: MatDialog,
    private translateService: TranslateService,
    private readonly contactCustomizationService: ContactTableCustomizationService,
    private readonly companyCustomizationService: CompanyTableCustomizationService,
    private readonly crmAssociationService: CrmAssociationService,
    private readonly snackService: SnackbarService,
    private readonly accessBusinessAppService: AccessBusinessAppService,
    private readonly defaultAssociationActionsService: DefaultAssociationActionsService,
  ) {
    this.associationService.setCrmObjectIdToAssociate(this.data?.id || '');
    this.fullName$ = objectService.getMultiObject('Contact', [this.data.id || '']).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return '';
        }
        return this.contactCustomizationService.getRowFullName(object || {});
      }),
    );

    this.getAssociations$ = this.associationService.getCompanyAssociationsForContact$(this.data.id || '');
    this.existingAssociations$ = this.associationService.getRemainingCompanyAssociationsForContacts$(
      this.data.id || '',
    );

    const latestCompanies$ = this.loadAssociations$$.pipe(
      switchMap(() =>
        combineLatest([
          this.getAssociations$,
          this.config.routePrefix$,
          this.loadAssociations$$,
          this.existingAssociations$,
          this.changedAssociations$$,
        ]),
      ),
      tap(() => {
        this.removedAssociationIds$$.next([]);
      }),
      map(([associations, routePrefix, copyCrmObject, existing, changed]) => {
        const associatedObjects = copyCrmObject.map((association) => {
          return {
            associationId: association.associationId,
            associationType: 'Contact-Company',
            tags: association.tags || [],
            associatedObject: association,
          } as Association;
        });

        const combinedData = [...associations, ...associatedObjects];
        const existingAssociationsCombined = [...existing, ...associatedObjects];
        this.preSelectedRows$$.next(existingAssociationsCombined);
        const dataWithChanges = combineAssociationsWithChanges(combinedData, changed);

        return dataWithChanges
          .filter((association, index, array) => {
            return (
              !!association.associatedObject &&
              array.findIndex((a) => a.associationId === association.associationId) === index
            );
          })
          .map((association) => {
            if (!association.associatedObject) {
              return {} as ContactToCompanyAssociationsCardData;
            }
            const contactName = this.data.name || '';
            const companyName =
              this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyName)
                ?.stringValue || '';
            const companyId =
              this.fieldService.getFieldValueFromCrmObject(association.associatedObject, SystemFieldIds.CompanyID)
                ?.stringValue || '';
            const data = {
              associationId: association.associationId,
              contactName: contactName,
              type: 'business',
              tags: association.tags || [],
              id: companyId,
              businessName: companyName,
              businessAddress: {
                address1: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressLine1,
                )?.stringValue,
                address2: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressLine2,
                )?.stringValue,
                city: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressCity,
                )?.stringValue,
                state: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressState,
                )?.stringValue,
                country: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressCountry,
                )?.stringValue,
                zip: this.fieldService.getFieldValueFromCrmObject(
                  association.associatedObject,
                  StandardIds.CompanyPrimaryAddressPostalCode,
                )?.stringValue,
              },
              businessWebsite: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.CompanyWebsite,
              )?.stringValue,
              phone: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.CompanyPhoneNumber,
              )?.stringValue,
              profileButtonUrl:
                `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
                  ':crmObjectId',
                  association.associatedObject.crmObjectId || '',
                ),
              businessAppAccess: this.accessBusinessAppService.getAppAccess(
                this.dialog,
                this.data.id || '',
                companyId,
                contactName,
                companyName,
              ),
            } as ContactToCompanyAssociationsCardData;
            return data;
          });
      }),
    );

    this.companies$ = combineLatest([latestCompanies$, this.removedAssociationIds$$.asObservable()]).pipe(
      map(([companies, removed]) => {
        this.hasAssociation = companies.length === removed.length ? false : true;
        return companies.filter((company) => {
          return !removed.includes(company.associationId);
        });
      }),
    );

    this.modalData$ = combineLatest([this.fullName$, this.preSelectedRows$$]).pipe(
      map(([fullName, preSelectedRows]) => {
        return {
          modalTitle: fullName,
          primarySelect: '',
          description: '',
          selectedRows: [],
          associationType: '',
          associations: preSelectedRows,
          newSelection: '',
        };
      }),
    );
  }

  openModal(): void {
    if (this.canOpenModal) {
      return;
    }
    this.canOpenModal = true;
    this.modalData$
      .pipe(
        take(1),
        tap(() => {
          this.canOpenModal = false;
        }),
        switchMap((data) => {
          const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
            data: {
              baseColumnIds: [],
              objectType: 'Company',
              modalTitle: this.translateService.instant('ASSOCIATIONS.MODAL.TITLE_CONTACT_MODAL', {
                contactFullName: data.modalTitle,
              }),
              associations: data.associations,
              associationType: 'Contact-Company',
            } as ListObjectTypeDialogData,
            injector: Injector.create({
              providers: [
                {
                  provide: PageAnalyticsInjectionToken,
                  useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
                },
                {
                  provide: GalaxyFilterChipInjectionToken,
                  useFactory: tableFiltersInjectionTokenGenerator('Company'),
                },
              ],
              parent: this.injector,
            }),
          });
          return dialogRef.afterClosed();
        }),
        map((result) => {
          return result?.selectedRows || new Array<Row>();
        }),
        filter((selectedRows) => !!selectedRows),
        switchMap((selectedRows: Row[]) => {
          const selectedCompaniesIDs = selectedRows.map((row) => {
            return row.id;
          });
          return this.objectService.getMultiObject('Company', selectedCompaniesIDs).pipe(
            map((resp) => {
              const copyCrmObjects = resp.crmObjects?.map((object) => object);
              return {
                copyCrmObjects,
                objects: resp.crmObjects?.map((object) => {
                  return {
                    id: object?.crmObjectId,
                    name: this.companyCustomizationService.getRowFullName(object || {}),
                  };
                }),
              };
            }),
          );
        }),
        tap((selectedObjects) => {
          const updatedObjects = {
            crmObjects: selectedObjects.copyCrmObjects || [],
            objects: selectedObjects.objects || [],
          };
          this.associationService.setCopyCrmObjects(updatedObjects);
        }),
        mergeMap((selectedObjects) => {
          if (this.hasAssociation) {
            return this.crmAssociationService.createContactCompanyAssociation$('Contact-Company');
          } else {
            const dialogRef = this.dialog.open(SelectAssociationsModalComponent, {
              data: {
                selectedRows: selectedObjects.objects,
                modalTitle: this.translateService.instant('ASSOCIATIONS.MODAL.TITLE_ASSOCIATION'),
                primarySelect: this.translateService.instant('ASSOCIATIONS.COMPANY_MODAL.PRIMARY_COMPANY'),
                description: this.translateService.instant('ASSOCIATIONS.COMPANY_MODAL.PRIMARY_DESCRIPTION'),
                associationType: 'Contact-Company',
                associations: [{}],
              } as AssociationsDialogData,
              injector: Injector.create({
                providers: [
                  {
                    provide: PageAnalyticsInjectionToken,
                    useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
                  },
                  {
                    provide: GalaxyFilterChipInjectionToken,
                    useFactory: tableFiltersInjectionTokenGenerator('Company'),
                  },
                ],
                parent: this.injector,
              }),
            });
            return dialogRef.afterClosed();
          }
        }),
      )
      .subscribe((copyCrmObjects) => {
        if (copyCrmObjects.respCount.error) {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
            duration: 10000,
            interpolateTranslateParams: {
              createdCount: copyCrmObjects.respCount.createdCount,
              totalCount: copyCrmObjects.respCount.totalCount,
            },
          });
        } else {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.GENERIC', {
            duration: 10000,
          });
        }
        this.loadAssociations$$.next(copyCrmObjects.copyCrmObjectsUpdated);
      });
  }

  removeAssociation(cardData: ContactToCompanyAssociationsCardData) {
    return this.dialog
      .open(RemoveAssociationDialogComponent, {
        width: '660px',
        data: {
          associationID: cardData.associationId,
          fromName: cardData.contactName,
          removeName: cardData.businessName,
        } as RemoveAssociationData,
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((success) => {
          if (success) {
            this.associationService.removeAssociation(cardData.associationId);
            this.removedAssociationIds$$.next([...this.removedAssociationIds$$.value, cardData.associationId]);
            this.preSelectedRows$$.next(
              this.preSelectedRows$$.getValue().filter((row) => row.associationId !== cardData.associationId),
            );
          }
        }),
      )
      .subscribe();
  }

  editTags(cardData: ContactToCompanyAssociationsCardData) {
    return this.defaultAssociationActionsService
      .editTagsAction(cardData)
      .pipe(
        take(1),
        tap((edited) => {
          if (edited === null) {
            return;
          }
          this.changedAssociations$$.next([edited]);
        }),
      )
      .subscribe();
  }
}
